import type { CSSProperties } from 'react';
import type { FulfillmentOrderShipmentSummary } from '@/infrastructure/FulfillmentAdminClient';

/**
 * Column widths and colour tokens for the fulfillment order list.
 *
 * The list carries 15 columns of very unequal width. Letting the browser distribute them starved the
 * Korean product name down to 78px, where CJK wraps one character per line and a single row grew to
 * 233px. The live OMS answers this by committing to a wide table and scrolling horizontally, so these
 * widths are declared up front and the table is laid out `fixed`.
 */
export interface FulfillmentOrderColumn {
  key: string;
  label: string;
  width: number;
  /** Numeric columns read better right-aligned; everything else stays left. */
  alignEnd?: boolean;
}

export const FULFILLMENT_ORDER_COLUMNS: FulfillmentOrderColumn[] = [
  // A 10-digit internal id and a full `dd/MM/yyyy HH:mm:ss` both overran 110px and spilled into Source; the comment
  // count chip beside the View Orders icon takes another 44px (SSD-7567).
  { key: 'orderId', label: 'Order ID', width: 184 },
  // A one-line "2026-10-08 11:19:06 KST" ran up to 39px past 175, and every row lost its time zone.
  { key: 'orderDate', label: 'Order Date', width: 215 },
  { key: 'source', label: 'Source', width: 150 },
  { key: 'country', label: 'Country', width: 90 },
  // The design's chip (11px/700, 0.3px tracking, 9px sides) carries AWAITING_INBOUND at 139px: 139 + 28 padding + 3.
  { key: 'status', label: 'Fulfillment status', width: 172 },
  { key: 'csStatus', label: 'CS status', width: 172 },
  // YUN_EXPRESS as the same chip is 104px.
  { key: 'provider', label: 'Provider', width: 136 },
  { key: 'providerOrderId', label: 'Provider Order ID', width: 200 },
  { key: 'pic', label: 'PIC', width: 130 },
  { key: 'customer', label: 'Customer', width: 240 },
  { key: 'shippingAddress', label: 'Shipping Address', width: 200 },
  // The head label itself is the widest thing in this column once the head is 13px/650 and never wraps.
  { key: 'totalValue', label: 'Total Value (Discount)', width: 160, alignEnd: true },
  { key: 'totalItems', label: 'Total Items', width: 100, alignEnd: true },
  { key: 'productNameKr', label: 'Product Name KR', width: 260 },
  { key: 'sku', label: 'SKU (Sourcing Route / Order Number / Tracking Number)', width: 400 },
  { key: 'trackingNo', label: 'Tracking No', width: 210 },
];

/** Width of the row-select column, which is always present. */
const SELECT_COLUMN_WIDTH = 48;

/**
 * Table width for the columns currently shown. Hiding a column has to shrink the table, or the
 * picker leaves the same horizontal scroll behind and buys the operator nothing.
 */
export const fulfillmentTableMinWidth = (columns: FulfillmentOrderColumn[]): number =>
  SELECT_COLUMN_WIDTH + columns.reduce((total, column) => total + column.width, 0);

const CARRIER_COLOURS: Record<string, string> = {
  DELEO: '#0D6EFD',
  DELEONEX: '#6610F2',
  YUN_EXPRESS: '#198754',
  YUN: '#198754',
};

/** Unrouted parcels stay grey — colour is reserved for a carrier that actually has the package. */
const CARRIER_FALLBACK = '#6C757D';

/**
 * Carrier identity as colour, mirroring the OMS list. Rendered on a plain `span.badge`: react-bootstrap's
 * Badge adds a `bg-*` class whose `!important` background paints over these tokens.
 */
export const carrierBadgeStyle = (provider: string | null | undefined): CSSProperties => ({
  backgroundColor: CARRIER_COLOURS[(provider ?? '').toUpperCase()] ?? CARRIER_FALLBACK,
  color: '#FFFFFF',
  fontWeight: 600,
});

/** Carrier codes operators read as a name; any other code shows as the server sent it. */
const CARRIER_LABELS: Record<string, string> = {
  // Deleonex is Deleo's platform, and its parcels are stored as DELEO, so both read as Deleo's own name.
  DELEO: 'Deleo',
  DELEONEX: 'Deleonex',
};

/** The carrier's name as the list badge shows it. */
export const carrierLabelOf = (provider: string): string => CARRIER_LABELS[provider.trim().toUpperCase()] ?? provider;

/** The carrier an outbound writes when no carrier holds the parcel yet; it names no carrier. */
const UNASSIGNED_CARRIER = 'UNASSIGNED';

/** That same outbound uses the parcel's own row id (`ord#<orderId>#OUT`) as its number; it is no waybill. */
const OUTBOUND_ROW_ID = /^ord#/i;

/**
 * The shipment as the list shows it. An order outbounded before its carrier returned a waybill (a Deleonex booking
 * still waiting for one) holds the outbound placeholder, and printing it read as carrier "UNASSIGNED" with a fake
 * waybill. Real values pass through unchanged.
 */
export const listedShipmentOf = (
  shipment: FulfillmentOrderShipmentSummary | null | undefined,
): FulfillmentOrderShipmentSummary => {
  const provider = shipment?.provider?.trim() ?? '';
  const trackingNumber = shipment?.trackingNumber?.trim() ?? '';
  return {
    provider: provider && provider.toUpperCase() !== UNASSIGNED_CARRIER ? provider : null,
    providerOrderId: shipment?.providerOrderId ?? null,
    trackingNumber: trackingNumber && !OUTBOUND_ROW_ID.test(trackingNumber) ? (shipment?.trackingNumber ?? null) : null,
  };
};

/** Terminal states are quiet, in-flight states are warm, and failures are the only red. */
const STATUS_TINTS: Record<string, { background: string; text: string }> = {
  OUTBOUNDED: { background: '#E7F5EC', text: '#0F5132' },
  CLOSED: { background: '#E7F5EC', text: '#0F5132' },
  READY: { background: '#FFF3CD', text: '#664D03' },
  AWAITING_INBOUND: { background: '#FFF3CD', text: '#664D03' },
  ORDERED: { background: '#E7F1FF', text: '#084298' },
  CANCELLED: { background: '#F1F3F5', text: '#495057' },
  MERGED: { background: '#F1F3F5', text: '#495057' },
  RETURNED: { background: '#FBE9E7', text: '#842029' },
  RETURNED_PARTIAL: { background: '#FBE9E7', text: '#842029' },
  RESOLVED: { background: '#E7F5EC', text: '#0F5132' },
};

const STATUS_TINT_FALLBACK = { background: '#F1F3F5', text: '#495057' };

/**
 * A soft pill rather than a saturated badge: a list where every row shouts has no emphasis left for
 * the row that matters.
 */
export const statusTintStyle = (status: string | null | undefined): CSSProperties => {
  // The ledger leaves a status unset on orders the pipeline has not classified; the row still has to draw.
  const tint = STATUS_TINTS[(status ?? '').toUpperCase()] ?? STATUS_TINT_FALLBACK;
  return { backgroundColor: tint.background, color: tint.text, fontWeight: 600 };
};
